// Angular
import {APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule} from '@angular/core';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {BrowserModule} from '@angular/platform-browser';
import {registerLocaleData} from '@angular/common';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
// Auth
import {
  AuthInterceptor as OidcAuthInterceptor,
  AuthModule,
  OidcSecurityService,
  OpenIdConfiguration,
  StsConfigHttpLoader,
  StsConfigLoader
} from 'angular-auth-oidc-client';
// Logging
import {LoggerModule, NGXLogger, NgxLoggerLevel} from 'ngx-logger';
// Portal
import {PortalMessageBroker} from './portal/portal-message-broker';
import {PortalTokenService} from './portal/portal-token.service';
// OpenAPI
import {ApiModule as ExportApiInternalModule, Configuration as ExportConfiguration} from './openapi/export-openapi';
// App
import {environment} from '../environments/environment';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {IconModule} from './modules/common/icon/icon.module';
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule} from '@angular/material/tooltip';
import {mergeMap, take, tap} from 'rxjs';
import {ConfigModule} from './config/config.module';
import {ConfigService} from './config/service/config.service';
import {filter, map} from 'rxjs/operators';
import {AuthInterceptor} from './auth/auth.interceptor';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import * as Sentry from '@sentry/angular-ivy';
import jwtDecode, {JwtPayload} from 'jwt-decode';
import {
  MatomoConsentMode,
  MatomoInitializationMode,
  MatomoInitializerService,
  NgxMatomoTrackerModule
} from '@ngx-matomo/tracker';
import {ContextInfoService, WellKnownJustFarmingProperties} from './portal/context-info.service';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatPaginatorIntl} from '@angular/material/paginator';
import {MatPaginatorLocalize} from './modules/common/mat-paginator-localize';
import {ConfirmationDialogModule} from './modules/common/confirmation-dialog/confirmation-dialog.module';
import {provideEffects} from '@ngrx/effects';
import {provideRouterStore} from '@ngrx/router-store';
import {provideState, provideStore} from '@ngrx/store';
import {provideStoreDevtools, StoreDevtoolsModule} from '@ngrx/store-devtools';
import {appReducers} from './store/app/app.reducers';
import {metaReducers} from './store/app/meta.reducers';
import {dialogsReducers} from './modules/dialogs/+store/dialogs.reducers';
import {FirmaEntitiesEffects} from './store/firma-entities/firma-entities.effects';
import {firmaEntitiesReducers} from './store/firma-entities/firma-entities.reducers';
import {datevWizardReducers} from './modules/routes/datev-wizard/+store/datev-wizard.reducers';
import {
  DatevWizardGetFirmaEffects
} from './modules/routes/datev-wizard/datev-wizard-get-firma/+store/datev-wizard-get-firma.effects';
import {CONTEXT_INFO, provideDialogs, RouterEffects} from '@adnova/jf-ng-components';
import {
  DialogWhereIsDatevClientIdSelectors
} from './modules/dialogs/dialog-where-is-datev-client-id/+store/dialog-where-is-datev-client-id.selectors';
import {
  DialogWhereIsDatevClientIdActions
} from './modules/dialogs/dialog-where-is-datev-client-id/+store/dialog-where-is-datev-client-id.actions';
import {
  DialogDeleteConfigurationSelectors
} from './modules/dialogs/dialog-delete-configuration/+store/dialog-delete-configuration.selectors';
import {
  DialogDeleteConfigurationActions
} from './modules/dialogs/dialog-delete-configuration/+store/dialog-delete-configuration.actions';
import {
  DatevWizardClientIdEffects
} from './modules/routes/datev-wizard/datev-wizard-client-id/+store/datev-wizard-client-id.effects';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {
  DatevWizardExportDateEffects
} from './modules/routes/datev-wizard/datev-wizard-export-date/+store/datev-wizard-export-date.effects';
import {
  DatevWizardConfigExistsEffects
} from './modules/routes/datev-wizard/datev-wizard-config-exists/+store/datev-wizard-config-exists.effects';
import {
  DatevWizardConfigDetailsEffects
} from './modules/routes/datev-wizard/datev-wizard-config-details/+store/datev-wizard-config-details.effects';
import {
  DatevWizardLoginEffects
} from './modules/routes/datev-wizard/datev-wizard-login/+store/datev-wizard-login.effects';
import {
  DatevWizardCaseSelectionEffects
} from './modules/routes/datev-wizard/datev-wizard-case-selection/+store/datev-wizard-case-selection.effects';
import {ErrorEventEntitiesEffects} from './store/error-event-entities/error-event-entities.effects';
import {errorEventEntitiesReducers} from './store/error-event-entities/error-event-entities.reducers';
import {ERROR_EVENT_ENTITIES_FEATURE_KEY} from './store/error-event-entities/error-event-entities.selectors';
import {FIRMA_ENTITIES_FEATURE_KEY} from './store/firma-entities/firma-entities.selectors';


registerLocaleData(localeDe, 'de-DE', localeDeExtra);

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    IconModule,
    ConfirmationDialogModule,
    // Angular
    BrowserAnimationsModule,
    BrowserModule,
    HttpClientModule,
    // Config
    ConfigModule,
    // Logging
    LoggerModule.forRoot({
      level: NgxLoggerLevel.INFO, // default logging level
      enableSourceMaps: !environment.production,
    }),
    // Auth
    // TODO: Auth ggf. in eigenes Module auslagern
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: (configService: ConfigService) => {
          const configObservable = configService.loaded$
            .pipe(
              take(1),
              map(config => {
                const authConfig: OpenIdConfiguration = {
                  // loaded
                  authority: config.auth.authority,
                  clientId: config.auth.clientId,
                  scope: config.auth.scope,
                  logLevel: config.auth.logLevel,
                  renewTimeBeforeTokenExpiresInSeconds: config.auth.renewTimeBeforeTokenExpiresInSeconds,
                  // predefined
                  redirectUrl: window.location.origin,
                  postLogoutRedirectUri: window.location.origin,
                  responseType: 'code',
                  silentRenew: true,
                  triggerAuthorizationResultEvent: true,
                  ignoreNonceAfterRefresh: true,
                  useRefreshToken: true,
                  // INFO: Bei anpassungen auch AuthInterceptor.matchesSecureRoots() anpassen
                  secureRoutes: [
                    config.exportConfig.apiBaseUrl,
                  ],
                };
                return authConfig;
              }),
            );
          return new StsConfigHttpLoader(configObservable);
        },
        deps: [ConfigService],
      },
    }),
    // OpenAPI
    ExportApiInternalModule.forRoot(() => new ExportConfiguration()),
    // App
    AppRoutingModule,
    NgxMatomoTrackerModule.forRoot({
      mode: MatomoInitializationMode.AUTO_DEFERRED,
      requireConsent: MatomoConsentMode.TRACKING,
    }),
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatTooltipModule,
    MatSnackBarModule,
  ],
  exports: [
    AuthModule,
  ],
  providers: [
    provideStore(
      appReducers,
      {
        metaReducers,
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictActionSerializability: true,
          strictStateSerializability: true,
        }
      },
    ),
    provideState('dialogs', dialogsReducers),
    provideState('wizard', datevWizardReducers),
    provideState(FIRMA_ENTITIES_FEATURE_KEY, firmaEntitiesReducers),
    provideState(ERROR_EVENT_ENTITIES_FEATURE_KEY, errorEventEntitiesReducers),
    provideEffects([
      RouterEffects,
      FirmaEntitiesEffects,
      DatevWizardGetFirmaEffects,
      DatevWizardCaseSelectionEffects,
      DatevWizardClientIdEffects,
      DatevWizardExportDateEffects,
      DatevWizardConfigExistsEffects,
      DatevWizardConfigDetailsEffects,
      DatevWizardLoginEffects,
      ErrorEventEntitiesEffects,
    ]),
    provideRouterStore(),
    provideDialogs([
      {
        key: 'whereIsDatevClientId',
        isOpenSelector: DialogWhereIsDatevClientIdSelectors.isOpen,
        closeAction: DialogWhereIsDatevClientIdActions.close,
        loader: () => import('./modules/dialogs/dialog-where-is-datev-client-id/dialog-where-is-datev-client-id.component')
          .then(c => c.DialogWhereIsDatevClientIdComponent),
        config: {
          width: '530px',
          minWidth: '530px',
          maxHeight: '400px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: true,
        },
      },
      {
        key: 'deleteConfiguration',
        isOpenSelector: DialogDeleteConfigurationSelectors.isOpen,
        closeAction: DialogDeleteConfigurationActions.close,
        loader: () => import('./modules/dialogs/dialog-delete-configuration/dialog-delete-configuration.component')
          .then(c => c.DialogDeleteConfigurationComponent),
        config: {
          width: '400px',
          minWidth: '400px',
          maxHeight: '400px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: true,
        },
      },
    ]),
    provideStoreDevtools({
      name: 'Export',
      maxAge: 25,
      logOnly: environment.production,
    }),
    {
      provide: CONTEXT_INFO,
      useClass: ContextInfoService,
    },
    OidcAuthInterceptor,
    {
      // INFO: Interceptor um automatisch da OAuth Token zu setzen
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      // INFO: Initializer das Logging
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigService, logger: NGXLogger) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(value => {
              logger.updateConfig({
                ...logger.getConfigSnapshot(),
                level: value.logging.level
              });
            }),
          );
      },
      deps: [ConfigService, NGXLogger],
      multi: true,
    },
    {
      // INFO: Initializer für Sentry Integration
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        contextInfoService: ContextInfoService,
        logger: NGXLogger,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(config => {
              const sentry = config.sentry;
              const info = environment.info;
              if (sentry) {
                Sentry.init({
                  enabled: sentry.enabled,
                  debug: sentry.debug,
                  environment: config.environment,
                  release: info.commit,
                  dsn: sentry.dsn,
                  sampleRate: sentry.sampleRate,
                  autoSessionTracking: false,
                  enableTracing: false,
                  // maxBreadcrumbs nur erhöhen, wenn niemals sensiblen Daten mitgeschrieben werden
                  // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/integrations/default/#breadcrumbs
                  maxBreadcrumbs: 0,
                  initialScope: {
                    extra: {
                      'info': info,
                    },
                  },
                  beforeSend: (event) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)) {
                      return event;
                    }
                    logger.debug('Error reporting is disabled, dropping event');
                    return null;
                  },
                  beforeSendTransaction: (event) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (
                      contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS)
                      && contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
                    ) {
                      return event;
                    }
                    logger.debug('Error reporting or analytics are disabled, dropping transaction event');
                    return null;
                  },
                  beforeBreadcrumb: (breadcrumb) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (
                      contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS)
                      && contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
                    ) {
                      return breadcrumb;
                    }
                    logger.debug('Error reporting or analytics are disabled, dropping breadcrumb');
                    return null;
                  },
                });
              }
            }),
          );
      },
      deps: [
        ConfigService,
        ContextInfoService,
        NGXLogger,
      ],
      multi: true,
    },
    {
      // INFO: Angular Sentry ErrorHandler
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        matomoInitializerService: MatomoInitializerService,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(config => {
              const matomoConfig = config.matomo;

              if (
                matomoConfig?.enabled
                && matomoConfig?.trackerUrl
                && matomoConfig?.siteId
              ) {
                matomoInitializerService.initializeTracker({
                  trackerUrl: matomoConfig.trackerUrl,
                  siteId: matomoConfig.siteId,
                });

              }
            }),
          );
      },
      deps: [
        ConfigService,
        MatomoInitializerService,
      ],
      multi: true,
    },
    {
      // INFO: Initializer der die Base-URL setzt
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        exportConfiguration: ExportConfiguration,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(value => {
              exportConfiguration.basePath = value.exportConfig.apiBaseUrl;
            }),
          );
      },
      deps: [ConfigService, ExportConfiguration],
      multi: true,
    },
    {
      // INFO: Initializer für Portal integration die Base-URL setzt
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        exportConfiguration: ExportConfiguration,
        portalMessageBroker: PortalMessageBroker,
        portalTokenService: PortalTokenService,
        oidcSecurityService: OidcSecurityService,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            mergeMap(config => {
              return fromPromise(portalMessageBroker.init(config.portal.targetOrigin))
                .pipe(map(() => config));
            }),
            mergeMap(config => {
              return fromPromise(portalTokenService.init(config.auth.renewTimeBeforeTokenExpiresInSeconds))
                .pipe(map(() => config));
            }),
            tap(() => {
              const runningEmbedded = portalMessageBroker.isRunningInPortal();
              Sentry.setTag('runningEmbedded', runningEmbedded);

              if (runningEmbedded) {
                const decoded = jwtDecode<JwtPayload>(portalTokenService.getAdnovaToken());
                const token = decoded as any;

                const email = token.email;
                const username = token.preferred_username;
                const name = token.name;

                Sentry.withScope(scope => {
                  let user = scope.getUser();
                  if (!user) {
                    user = {};
                  }
                  user.id = decoded.sub;
                  user.email = email;
                  user.username = username;
                  user['name'] = name;

                  scope.setUser(user);
                });
              } else {
                oidcSecurityService.getAccessToken()
                  .pipe(
                    filter(value => {
                      if (value) {
                        return true;
                      }
                      return false;
                    }),
                  )
                  .subscribe(accessToken => {
                    const decoded = jwtDecode<JwtPayload>(accessToken);
                    const token = decoded as any;

                    const email = token.email;
                    const username = token.preferred_username;
                    const name = token.name;

                    Sentry.withScope(scope => {
                      let user = scope.getUser();
                      if (!user) {
                        user = {};
                      }
                      user.id = decoded.sub;
                      user.email = email;
                      user.username = username;
                      user['name'] = name;

                      scope.setUser(user);
                    });
                  });
              }
            }),
          );
      },
      deps: [
        ConfigService,
        ExportConfiguration,
        PortalMessageBroker,
        PortalTokenService,
        OidcSecurityService
      ],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        disableTooltipInteractivity: true,
        showDelay: 600,
        hideDelay: 150,
      },
    },
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginatorLocalize,
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
